// Free geocoding via public APIs (no API key needed). The host page's CSP
// must allow these origins — they're already in the POC's connect-src list:
//   https://api.zippopotam.us — zip → city + lat/long
//   https://api.bigdatacloud.net — coords ↔ reverse geocode
//
// For the widget's "type your zip" entry point we only need the zip→coords
// path. Reverse-geocoding (for an HTML5 geolocation flow) is deferred.

interface ZippopotamResponse {
  'post code': string;
  country: string;
  places: Array<{
    'place name': string;
    state: string;
    'state abbreviation': string;
    longitude: string;
    latitude: string;
  }>;
}

export interface ZipGeocode {
  zip: string;
  city: string;
  state: string;
  stateAbbr: string;
  lat: number;
  lng: number;
}

export async function geocodeZip(zip: string): Promise<ZipGeocode | null> {
  const clean = zip.trim();
  if (!/^\d{5}$/.test(clean)) return null;

  try {
    const res = await fetch(`https://api.zippopotam.us/us/${clean}`);
    if (!res.ok) return null;
    const data = (await res.json()) as ZippopotamResponse;
    const place = data.places[0];
    if (!place) return null;
    return {
      zip: data['post code'],
      city: place['place name'],
      state: place.state,
      stateAbbr: place['state abbreviation'],
      lat: parseFloat(place.latitude),
      lng: parseFloat(place.longitude),
    };
  } catch {
    return null;
  }
}

// Simple debounce helper for autocomplete-as-you-type
export function debounce<F extends (...args: never[]) => void>(fn: F, ms: number): F {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return ((...args: never[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as F;
}
